(() => {
  const descriptionSelector = '[data-product-description-accordion]';
  const headingTags = new Set(['H2', 'H3', 'H4', 'H5', 'H6']);
  const bracketTitlePattern = /^\s*【([^】]+)】\s*/;
  const maximumTitleLength = 180;
  let accordionIndex = 0;

  const hasVisibleContent = (elements) =>
    elements.some(
      (element) =>
        element.textContent.trim() ||
        element.querySelector('img, video, iframe, table, ul, ol, blockquote')
    );

  const isStandaloneBoldTitle = (element) => {
    if (!['P', 'DIV'].includes(element.tagName)) return false;

    const title = element.textContent.trim();
    if (!title || title.length > maximumTitleLength || !element.querySelector('b, strong')) return false;

    return Array.from(element.childNodes).every((node) => {
      if (node.nodeType === Node.TEXT_NODE) return !node.textContent.trim();
      if (node.nodeType === Node.COMMENT_NODE) return true;
      if (node.nodeType !== Node.ELEMENT_NODE) return false;

      return node.matches('b, strong, br');
    });
  };

  const isDescriptionTitle = (element) => {
    const title = element.textContent.trim();
    if (!title || title.length > maximumTitleLength) return false;

    return headingTags.has(element.tagName) || isStandaloneBoldTitle(element);
  };

  const createCaret = () => {
    const namespace = 'http://www.w3.org/2000/svg';
    const caret = document.createElementNS(namespace, 'svg');
    const path = document.createElementNS(namespace, 'path');

    caret.setAttribute('aria-hidden', 'true');
    caret.setAttribute('focusable', 'false');
    caret.setAttribute('class', 'icon icon-caret');
    caret.setAttribute('viewBox', '0 0 10 6');

    path.setAttribute('fill-rule', 'evenodd');
    path.setAttribute('clip-rule', 'evenodd');
    path.setAttribute(
      'd',
      'M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z'
    );
    path.setAttribute('fill', 'currentColor');
    caret.append(path);

    return caret;
  };

  const createAccordionItem = (group, baseId, itemIndex) => {
    const item = document.createElement('div');
    const details = document.createElement('details');
    const summary = document.createElement('summary');
    const summaryTitle = document.createElement('div');
    const title = document.createElement('h3');
    const content = document.createElement('div');
    const summaryId = `${baseId}-summary-${itemIndex}`;
    const contentId = `${baseId}-content-${itemIndex}`;
    const initiallyOpen = itemIndex === 0;

    item.className =
      'product__accordion product-accordion accordion no-padding product-description-accordion__item';

    details.open = initiallyOpen;

    summary.id = summaryId;
    summary.className = 'product-description-accordion__summary';
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', initiallyOpen ? 'true' : 'false');
    summary.setAttribute('aria-controls', contentId);

    summaryTitle.className = 'summary-title';
    title.className = 'accordion__title medium';
    title.innerHTML = group.title.innerHTML;
    summaryTitle.append(title);
    summary.append(summaryTitle, createCaret());

    content.id = contentId;
    content.className =
      'collapsible__content accordion__content rte product-description-accordion__content';
    content.setAttribute('role', 'region');
    content.setAttribute('aria-labelledby', summaryId);
    content.append(...group.content);

    details.addEventListener('toggle', () => {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    });
    details.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape' || !details.open) return;

      details.open = false;
      summary.focus();
    });

    details.append(summary, content);
    item.append(details);

    return item;
  };

  const createHeadingGroups = (children) => {
    const titleIndexes = children.reduce((indexes, element, index) => {
      if (isDescriptionTitle(element)) indexes.push(index);
      return indexes;
    }, []);

    if (titleIndexes.length < 2) return null;

    const groups = titleIndexes.map((titleIndex, index) => {
      const nextTitleIndex = titleIndexes[index + 1] ?? children.length;

      return {
        title: children[titleIndex],
        content: children.slice(titleIndex + 1, nextTitleIndex),
      };
    });

    if (groups.some((group) => !hasVisibleContent(group.content))) return null;

    return {
      intro: children.slice(0, titleIndexes[0]),
      groups,
    };
  };

  const removeLeadingText = (element, characterCount) => {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let remaining = characterCount;
    let textNode = walker.nextNode();

    while (textNode && remaining > 0) {
      const text = textNode.textContent;

      if (text.length <= remaining) {
        remaining -= text.length;
        textNode.textContent = '';
      } else {
        textNode.textContent = text.slice(remaining);
        remaining = 0;
      }

      textNode = walker.nextNode();
    }
  };

  const createBracketedListGroups = (children) => {
    if (children.length !== 1 || !children[0].matches('ul, ol')) return null;

    const listItems = Array.from(children[0].children);
    if (listItems.length < 2 || listItems.some((item) => item.tagName !== 'LI')) return null;

    const groups = listItems.map((listItem) => {
      const match = listItem.textContent.match(bracketTitlePattern);
      if (!match || !match[1].trim()) return null;

      const title = document.createElement('p');
      const titleText = document.createElement('strong');
      const content = document.createElement('div');

      titleText.textContent = match[1].trim();
      title.append(titleText);
      content.append(...Array.from(listItem.childNodes, (node) => node.cloneNode(true)));
      removeLeadingText(content, match[0].length);

      if (!hasVisibleContent([content])) return null;

      return {
        title,
        content: [content],
      };
    });

    if (groups.some((group) => group === null)) return null;

    return {
      intro: [],
      groups,
    };
  };

  const enhanceDescription = (description) => {
    if (description.dataset.productDescriptionAccordionReady === 'true') return;

    const children = Array.from(description.children);
    const accordionData =
      createHeadingGroups(children) || createBracketedListGroups(children);

    if (!accordionData) return;

    const accordion = document.createElement('div');
    const baseId = `ProductDescriptionAccordion-${accordionIndex++}`;

    accordion.className = 'product-description-accordion';
    accordionData.groups.forEach((group, index) => {
      accordion.append(createAccordionItem(group, baseId, index));
    });

    description.replaceChildren(...accordionData.intro, accordion);
    description.classList.add('product-description--accordion');
    description.dataset.productDescriptionAccordionReady = 'true';
  };

  const enhanceDescriptions = (root = document) => {
    const descriptions = [];

    if (root.matches?.(descriptionSelector)) descriptions.push(root);
    descriptions.push(...root.querySelectorAll(descriptionSelector));
    descriptions.forEach(enhanceDescription);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhanceDescriptions());
  } else {
    enhanceDescriptions();
  }

  document.addEventListener('shopify:section:load', (event) => {
    enhanceDescriptions(event.target);
  });
})();
